import { HL } from "../kernel.js";

/**
 * Globe: a desk globe with nothing mapped on it. A bare graticule on a sphere
 * tilted 23° on its axis, held at the poles by a meridian ring that stands on
 * a short stem and a round foot. A flick across the globe spins it about its
 * own axis; friction bleeds the spin and a detent every 60° catches it on one
 * of six stops. At rest the equator is the bright line; while the globe is
 * handled the bright goes to the meridian at the front, and the read-out names
 * the stop and the nothing it holds. The slider is the coast: friction's time
 * constant, in ms.
 *
 * The pattern: push the camera, as Turntable's: friction, then a spring into
 * the detent, and a hit test on the sphere's outline, which spinning never moves.
 */
const {
  Cam, circ, clamp, facing, fit, lerp, open, poly, prism, proj, rad, reducedMotion, seg,
  disposer, mk, place, pointer, put, register, solid,
} = HL;

const R = 52, RI = 59, RW = 5, RO = RI + RW, TILT = 23, RA = 0, ZC = 104, S = 1.48;
const STEP = 60, MER = 30, NM = 360 / MER, STOPS = 360 / STEP, OFF = -20, WMAX = 540;
const FOOT = 34, FH = 5, BOSS = 12, BH = 4, STEM = 2.8, LATS = [-60, -30, 30, 60];

const add = (p, q, k = 1) => [p[0] + q[0] * k, p[1] + q[1] * k, p[2] + q[2] * k];
const dot = (p, q) => p[0] * q[0] + p[1] * q[1] + p[2] * q[2];
const cross = (p, q) => [p[1] * q[2] - p[2] * q[1], p[2] * q[0] - p[0] * q[2], p[0] * q[1] - p[1] * q[0]];
const unit = (p) => { const l = Math.hypot(...p); return p.map((v) => v / l); };
const deg = (r) => (r * 180) / Math.PI;
const mod = (n, m) => ((n % m) + m) % m;

/** The runs of a sampled line that face the viewer, as open polylines; a closed line starts at a hidden sample. */
function arcs(pts, seen, closed) {
  let start = 0;
  if (closed) { start = seen.indexOf(false); if (start < 0) return poly(pts); }
  let d = "", cur = [];
  for (let i = 0; i < pts.length + (closed ? 1 : 0); i++) {
    const k = (start + i) % pts.length;
    if (seen[k]) cur.push(pts[k]);
    else { if (cur.length > 1) d += open(cur); cur = []; }
  }
  return cur.length > 1 ? d + open(cur) : d;
}

function mount({ stage, svg, read }, value) {
  const bag = disposer();
  let coast = value;

  const C = Cam(45, 0.5, S), zf = Math.sqrt(1 - C.k * C.k);
  // toward the viewer, and the two screen directions across it
  const V = [Math.sin(C.az) * zf, Math.cos(C.az) * zf, C.k], U = [Math.cos(C.az), -Math.sin(C.az), 0], W = cross(V, U);
  const O = [0, 0, ZC], Z = [0, 0, 1], H = [Math.cos(rad(RA)), Math.sin(rad(RA)), 0];
  // the axis leans TILT in the ring's plane; b1, b2 span the equator
  const A = add([0, 0, Math.cos(rad(TILT))], H, Math.sin(rad(TILT))), B1 = add([0, 0, -Math.sin(rad(TILT))], H, Math.cos(rad(TILT))), B2 = cross(A, B1);
  const sph = (lat, lon) => add(add(B1.map((v) => v * Math.cos(lat) * Math.cos(lon)), B2, Math.cos(lat) * Math.sin(lon)), A, Math.sin(lat));
  const ringAt = (t, r) => add(O, add(H.map((v) => v * Math.cos(t)), Z, Math.sin(t)), r);
  const limb = [];
  for (let i = 0; i < 96; i++) { const t = (i / 96) * 2 * Math.PI; limb.push(add(O, add(U.map((v) => v * Math.cos(t)), W, Math.sin(t)), R)); }
  const ext = limb.concat(circ(FOOT, 24).map((q) => [q.u, q.v, 0]));
  for (let i = 0; i < 36; i++) ext.push(ringAt(rad(i * 10), RO));
  fit(C, ext, 200, 166);
  const P = proj(C), front = facing(C), Pw = (p) => P(p[0], p[1], p[2]);

  // the meridian at the front, a little off centre so no line runs straight down the face
  const N = unit(add(V, A, -dot(V, A))), HOME = deg(Math.atan2(dot(N, B2), dot(N, B1))) + OFF;
  const detent = (a) => HOME + STEP * Math.round((a - HOME) / STEP);
  const stopOf = (a) => mod(Math.round((HOME - a) / STEP), STOPS) + 1;
  const litOf = (a) => mod(Math.round((HOME - a) / MER), NM);

  const g = mk("g", {}, svg);
  // the stand: a round foot, a boss, the stem up into the ring's cradle
  put(solid(g), prism(P, front, circ(FOOT, 48), circ(FOOT - 2, 48), 0, FH));
  put(solid(g), prism(P, front, circ(BOSS, 32), circ(BOSS - 1.2, 32), FH, FH + BH));
  put(solid(g), prism(P, front, circ(STEM, 16), circ(STEM - 0.8, 16), FH + BH, ZC - RO + 1.5));

  // the meridian ring, a flat band in its own plane: its far half behind the sphere, its near half in front
  const tf = deg(Math.atan2(V[2], dot(V, H)));
  function band(t0) {
    const ts = [];
    for (let i = 0; i <= 72; i++) ts.push(rad(t0 + i * 2.5));
    const out = ts.map((t) => Pw(ringAt(t, RO))), inn = ts.map((t) => Pw(ringAt(t, RI)));
    mk("path", { class: "fo", d: poly(out.concat(inn.slice().reverse())) }, g);
    mk("path", { class: "nf", d: open(inn) }, g);
    mk("path", { class: "nf sil", d: open(out) }, g);
  }
  const tN = rad(90 - TILT), tS = tN + Math.PI;
  band(tf + 90);
  mk("path", { class: "nf", d: seg(Pw(add(O, A, -R)), Pw(ringAt(tS, RI))) }, g);

  // the sphere, and its graticule: parallels and the equator stand still as it spins, meridians turn
  mk("path", { class: "sil", d: poly(limb.map(Pw)) }, g);
  const parallel = (lat) => {
    const pts = [], seen = [];
    for (let i = 0; i < 120; i++) { const d = sph(rad(lat), (i / 120) * 2 * Math.PI); pts.push(Pw(add(O, d, R))); seen.push(dot(d, V) > 0); }
    return arcs(pts, seen, true);
  };
  for (const lat of LATS) mk("path", { class: "nf lo", d: parallel(lat) }, g);
  const eq = mk("path", { class: "nf hi", d: parallel(0) }, g);
  const mer = [];
  for (let j = 0; j < NM; j++) mer.push(mk("path", { class: j ? "nf lo" : "nf" }, g));
  mk("path", { class: "nf", d: seg(Pw(add(O, A, R)), Pw(ringAt(tN, RI))) }, g);
  band(tf - 90);
  const tk = [];
  for (let t = Math.ceil((tf - 85) / 10) * 10; t <= tf + 85; t += 10) tk.push(seg(Pw(ringAt(rad(t), RO - 2.2)), Pw(ringAt(rad(t), RO - 0.6))));
  mk("path", { class: "nf lo", d: tk.join("") }, g);
  place(mk("circle", { r: 1.6, class: "dot m" }, g), Pw(ringAt(tN, RI + RW / 2)));

  // the globe's turn about its axis in degrees, its speed, and whether it is coasting, settling or at rest
  const spin = { a: HOME, w: 0, mode: "rest", tgt: HOME };
  let last = null, lastT = 0, lastMove = -1e9, over = false, drawnA = NaN, lit = -2, said = "";

  /** Coast under friction, then spring into the detent; 240Hz substeps so a long frame can't overshoot. */
  function stepSpin(dt, now) {
    if (spin.mode === "rest") return false;
    const n = Math.max(1, Math.ceil(dt * 240)), h = dt / n;
    for (let i = 0; i < n; i++) {
      if (spin.mode === "coast") {
        spin.w *= Math.exp((-h * 1000) / coast); spin.a += spin.w * h;
        if (Math.abs(spin.w) < 45 && now - lastMove > 90) { spin.mode = "settle"; spin.tgt = detent(spin.a + spin.w * 0.2); }
      } else { spin.w += (-90 * (spin.a - spin.tgt) - 16 * spin.w) * h; spin.a += spin.w * h; }
    }
    if (spin.mode === "settle" && Math.abs(spin.a - spin.tgt) < 0.02 && Math.abs(spin.w) < 0.3) { spin.a = spin.tgt; spin.w = 0; spin.mode = "rest"; }
    return spin.mode !== "rest";
  }

  function drawMeridians() {
    if (spin.a === drawnA) return;
    drawnA = spin.a;
    mer.forEach((el, j) => {
      const lon = rad(j * MER + spin.a), pts = [], seen = [];
      for (let i = 0; i <= 64; i++) { const d = sph(rad(-80 + i * 2.5), lon); pts.push(Pw(add(O, d, R))); seen.push(dot(d, V) > 0); }
      el.setAttribute("d", arcs(pts, seen, false));
    });
  }

  /** One bright line: the equator at rest, the meridian at the front while the globe is handled or turning. */
  function light() {
    const busy = over || spin.mode !== "rest", want = busy ? litOf(spin.a) : -1;
    if (want !== lit) {
      lit = want;
      eq.setAttribute("class", want < 0 ? "nf hi" : "nf");
      mer.forEach((el, j) => el.setAttribute("class", j === want ? "nf hi" : j ? "nf lo" : "nf"));
    }
    const say = busy ? `stop ${stopOf(spin.a)} · 0` : "rest";
    if (say !== said) read.textContent = said = say;
  }

  const B = register(stage, (dt, now) => {
    const m = stepSpin(dt, now);
    drawMeridians();
    light();
    return m;
  });
  bag.add(B.unregister);

  // the sphere's outline never moves, so the pointer is tested against it; the front of the globe follows the pointer
  const o = Pw(O), f1 = Pw(add(O, cross(A, N), 1)), fl = Math.hypot(f1[0] - o[0], f1[1] - o[1]);
  const FD = [(f1[0] - o[0]) / fl, (f1[1] - o[1]) / fl], REACH = (RO + 4) * S;

  function leave() {
    over = false; last = null;
    if (reducedMotion()) { spin.a = detent(spin.a); spin.w = 0; spin.mode = "rest"; }
    B.wake();
  }

  bag.add(pointer(stage, {
    move: (p) => {
      if (Math.hypot(p[0] - o[0], p[1] - o[1]) > REACH) return leave();
      const now = performance.now();
      over = true;
      if (last !== null) {
        const dv = (p[0] - last[0]) * FD[0] + (p[1] - last[1]) * FD[1];
        if (reducedMotion()) spin.a += dv * 0.9;
        else {
          const vp = dv / Math.max(0.008, (now - lastT) / 1000);
          spin.w = clamp(lerp(spin.w, vp * 0.9, 0.35), -WMAX, WMAX); spin.mode = "coast";
        }
        if (Math.abs(dv) > 0.5) lastMove = now;
      }
      last = p; lastT = now; B.wake();
    },
    leave,
  }));
  bag.add(() => svg.replaceChildren());

  return { set: (v) => { coast = v; B.wake(); }, destroy: bag.dispose };
}

export default {
  name: "globe",
  means: "A bare desk globe on its tilted axis: flick it and it spins, coasts and catches on a stop with nothing mapped.",
  rules: [1, 4, 5, 6],
  range: [200, 650, 1500],
  tour: [[150, 140], [250, 140], [125, 150], null],
  mount,
};
